// src/page/InformationEdit.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

function InformationEdit() {
    useAuthGuard();
    const { informationID } = useParams();

    // form states
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [publishStatus, setPublishStatus] = useState('0'); // '0': 非公開, '1': 公開
    const [popupStatus, setPopupStatus] = useState('0');     // '0': OFF, '1': ON
    const [publicRange, setPublicRange] = useState('');      // "YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (s) => {
        if (!s) return '';
        const d = new Date(s);
        if (isNaN(d)) return '';
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const fpRef = useRef(null);

    // init flatpickr
    useEffect(() => {
        if (!window.flatpickr) return;
        fpRef.current = window.flatpickr('#public_range', {
            mode: 'range',
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onClose: (selectedDates) => {
                if (selectedDates.length === 2) {
                    const f = (d) =>
                        `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    const [s, e] = selectedDates;
                    setPublicRange(`${f(s)} ~ ${f(e)}`);
                }
            }
        });
        return () => { try { fpRef.current?.destroy(); } catch { } };
    }, []);

    // reflect controlled value -> picker
    useEffect(() => {
        if (!fpRef.current) return;
        if (!publicRange.includes('~')) return;
        const [a, b] = publicRange.split('~').map(s => s.trim());
        fpRef.current.setDate([a, b], true);
    }, [publicRange]);

    // fetch detail
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token');
                const res = await fetch(`${API_BASE_URL}/v1/information/${informationID}`, {
                    headers: { Authorization: token ? `Bearer ${token}` : '', Accept: 'application/json' },
                    signal: ac.signal
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const it = data.item ?? data;

                setTitle(it.informationTitle || '');
                setContent(it.informationDescription || '');
                setPublishStatus(it.publishedFlag ? '1' : '0');
                setPopupStatus(it.popupFlg ? '1' : '0');

                const start = fmt(it.publishedStartDate);
                const end = fmt(it.publishedEndDate);
                setPublicRange(start && end ? `${start} ~ ${end}` : '');
            } catch (e) {
                if (e.name !== 'AbortError') setError('読み込みに失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [informationID]);

    // ---- validation helpers ----
    const RANGE_RE =
        /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$/;

    const parseDateTime = (str) => {
        const [datePart, timePart] = str.trim().split(' ');
        const [yyyy, mm, dd] = datePart.split('/').map(Number);
        const [HH, ii] = timePart.split(':').map(Number);
        const d = new Date(yyyy, mm - 1, dd, HH, ii, 0, 0);
        return isNaN(d) ? null : d;
    };

    // ---- submit (PUT) ----
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const errs = [];
        const t = title.trim();
        const c = content.trim();
        if (!t) errs.push('タイトルは必須です。');
        // if (t.length > 20) errs.push('タイトルは20文字以内で入力してください。');
        if (!c) errs.push('内容は必須です。');
        // if (c.length > 2048) errs.push('内容は2048文字以内で入力してください。');
        if (publishStatus !== '0' && publishStatus !== '1') errs.push('公開設定を選択してください。');
        if (popupStatus !== '0' && popupStatus !== '1') errs.push('ポップアップフラグを選択してください。');

        let startISO = '', endISO = '';
        if (!publicRange) {
            errs.push('公開期間を入力してください。');
        } else if (!RANGE_RE.test(publicRange)) {
            errs.push('公開期間の形式が正しくありません。YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm で入力してください。');
        } else {
            const [lhs, rhs] = publicRange.split('~').map(s => s.trim());
            const d1 = parseDateTime(lhs);
            const d2 = parseDateTime(rhs);
            if (!d1 || !d2) {
                errs.push('公開期間の日時が不正です。');
            } else if (d1.getTime() > d2.getTime()) {
                errs.push('公開開始は公開終了以前である必要があります。');
            } else {
                startISO = d1.toISOString();
                endISO = d2.toISOString();
            }
        }

        if (errs.length) {
            alert(errs.join('\n'));
            return;
        }

        const payload = {
            informationTitle: t,
            informationDescription: c,
            publishedFlag: publishStatus === '1',
            popupFlg: popupStatus === '1',
            publishedStartDate: startISO,
            publishedEndDate: endISO
        };

        try {
            setSaving(true);
            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/information/${informationID}`, {
                method: 'PUT',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const msg = `更新に失敗しました (HTTP ${res.status})`;
                try {
                    const j = await res.json();
                    throw new Error(j.error || msg);
                } catch {
                    throw new Error(msg);
                }
            }
            alert('更新しました。');
            window.history.back();
        } catch (e) {
            alert(e.message || '更新に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    // ---- delete (DELETE) ----
    const handleDelete = async () => {
        if (deleting) return;
        if (!window.confirm('本当に削除しますか？この操作は取り消せません。')) return;

        try {
            setDeleting(true);
            const token = localStorage.getItem('access_token');
            const res = await fetch(`${API_BASE_URL}/v1/information/${informationID}`, {
                method: 'DELETE',
                headers: {
                    Authorization: token ? `Bearer ${token}` : '',
                    Accept: 'application/json'
                }
            });
            if (!res.ok) throw new Error(`削除に失敗しました (HTTP ${res.status})`);
            alert('削除しました。');
            window.history.back();
        } catch (e) {
            alert(e.message || '削除に失敗しました。');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="/information">お知らせ管理</a></li>
                        <li className="breadcrumb-item active">編集</li>
                    </ol>
                </nav>

                {/* HTML structure unchanged; only handlers/attrs added */}
                <form className="mb-9" onSubmit={handleSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto">
                            <h2 className="mb-2">お知らせ編集</h2>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0"
                                type="button"
                                onClick={() => window.history.back()}
                                disabled={saving || deleting}
                            >キャンセル</button>
                            <button
                                className="btn btn-primary me-2 mb-2 mb-sm-0"
                                type="submit"
                                disabled={saving || deleting}
                            >{saving ? '保存中...' : '更新'}</button>
                            <button
                                className="btn btn-danger mb-2 mb-sm-0"
                                type="button"
                                onClick={handleDelete}
                                disabled={saving || deleting}
                            >{deleting ? '削除中...' : '削除'}</button>
                        </div>
                    </div>

                    <div className="row g-5">
                        <div className="col-12 col-xl-8">

                            {/* ID */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">お知らせID</h4>
                                        <input className="form-control" type="text" value={informationID} disabled />
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <h4 className="mb-3">タイトル<span className="text-danger">*</span></h4>
                                <input
                                    className="form-control"
                                    type="text"
                                    name="voucher_name"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    maxLength={20}
                                />
                            </div>
                            <div className="mb-4">
                                <h4 className="mb-3">内容<span className="text-danger">*</span></h4>
                                <textarea
                                    className="form-control"
                                    name="content"
                                    rows="4"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    required
                                    maxLength={2048}
                                ></textarea>
                            </div>
                            {loading && <div className="text-body-secondary">読み込み中...</div>}
                            {!loading && error && <div className="text-danger">{error}</div>}
                        </div>

                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12 col-xl-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">公開設定<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="publish_status"
                                                                    value="0"
                                                                    checked={publishStatus === '0'}
                                                                    onChange={() => setPublishStatus('0')}
                                                                    required
                                                                /> 非公開
                                                            </label>
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="publish_status"
                                                                    value="1"
                                                                    checked={publishStatus === '1'}
                                                                    onChange={() => setPublishStatus('1')}
                                                                /> 公開
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <h5 className="mb-3">ポップアップフラグ<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="popup_status"
                                                                    value="0"
                                                                    checked={popupStatus === '0'}
                                                                    onChange={() => setPopupStatus('0')}
                                                                    required
                                                                /> OFF
                                                            </label>
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="popup_status"
                                                                    value="1"
                                                                    checked={popupStatus === '1'}
                                                                    onChange={() => setPopupStatus('1')}
                                                                /> ON
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">
                                                                公開期間<span className="text-danger">*</span>
                                                            </h5>
                                                        </div>
                                                        <input
                                                            className="form-control"
                                                            type="text"
                                                            name="public_range"
                                                            id="public_range"
                                                            placeholder="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm"
                                                            value={publicRange}
                                                            onChange={(e) => setPublicRange(e.target.value)}
                                                            required
                                                            pattern="^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2} ~ \d{4}\/\d{2}\/\d{2} \d{2}:\d{2}$"
                                                            title="YYYY/MM/DD HH:mm ~ YYYY/MM/DD HH:mm の形式で入力してください"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </form>
            </>
        </Layout>
    );
}

export default InformationEdit;
